import React from 'react';
import './message.scss';
import Icon, { IconType } from '../icon/Icon';
import Link, { LinkProps, LinkVariant } from '../link/Link';
import classNames from 'classnames';

export type MessageType = 'info' | 'success' | 'warning' | 'error';

export type MessageAction = LinkProps & {
  variant: LinkVariant.BUTTON | LinkVariant.LINK;
  shouldClose?: boolean;
};

export type MessageVariant = 'default' | 'light';

export interface MessageProps {
  type?: MessageType;
  description: React.ReactNode;
  actions?: MessageAction[];
  className?: string;
  icon?: IconType;
  iconCircle?: boolean;
  loadingIcon?: boolean;
  variant?: MessageVariant;
  shouldTurnicate?: boolean;
  onActionClick?: () => void;
}

const Message: React.FC<MessageProps> = ({
  type = 'info',
  description,
  actions = [],
  className = '',
  icon,
  iconCircle = false,
  loadingIcon = false,
  variant = 'default',
  shouldTurnicate = false,
  onActionClick,
}) => {
  const [isVisible, setIsVisible] = React.useState(true);

  if (!isVisible) return null;

  const variantSuffix = variant === 'light' ? '-light' : '';

  const handleClose = () => {
    if (onActionClick) {
      onActionClick();
    } else {
      setIsVisible(false);
    }
  };

  return (
    <section
      className={classNames(
        `message message--${type}${variantSuffix}`,
        className
      )}
    >
      <span className="message__icon" aria-hidden>
        {loadingIcon && iconCircle ? (
          <span className="message__icon-circle">
            <span className="message__icon--spinning-loader" />
          </span>
        ) : loadingIcon ? (
          <span className="message__icon--spinning-loader" />
        ) : iconCircle && icon ? (
          <span className="message__icon-circle">
            <Icon iconType={icon} className="u-icon-small" />
          </span>
        ) : icon ? (
          <Icon iconType={icon} className="u-icon-small" />
        ) : null}
      </span>

      <span
        className={classNames('message__text', {
          'message__text--truncate': shouldTurnicate,
        })}
        dangerouslySetInnerHTML={{ __html: description as string }}
      />

      {actions.length > 0 && (
        <span className="message__actions">
          {actions.map((action, i) => {
            return (
              <Link
                key={i}
                {...action}
                onClick={(e) => {
                  action.onClick?.(e);
                  if (action.shouldClose) {
                    onActionClick?.();
                    handleClose();
                  }
                }}
                isSmall
              />
            );
          })}
        </span>
      )}
    </section>
  );
};

export default Message;
